/**
 * Shared wiring for the DWG "View drawing" / "Compare drawings" actions.
 *
 * Mirrors `useIfcDiffOverview`: the selection lives in the MST selection model
 * (passed in as the ordered `selectedIds`), is derived here once, and flows
 * into the bulk-action bar / context menu as booleans + handlers — never
 * re-derived per action.
 *
 * Gate: the actions open the Drawings tool, gated on VIEW_DRAWING_COMPARE_TOOL
 * (PermissionProtectedRoute on every route to it) — pass
 * `enabled: canViewDrawingCompareTool` so a user without the grant doesn't see
 * actions that would bounce. The server enforces independently and more
 * narrowly: `request_drawing_geometry` requires the project-scoped
 * VIEW_DRAWING_COMPARE, and document RLS applies throughout. So this boolean is
 * a UI affordance, never the security boundary — a user holding the module
 * grant but not assigned to the drawing's project still gets a 42501.
 */

import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { orderCandidatePair } from "@/components/compare/fileCandidates";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { fetchSeedDocs } from "@/domains/drawing-compare/repository";
import { canPreviewDocumentRow } from "@/utils/previewUtils";

/** The document fields the drawing actions need — nothing more. The version
 * fields order a compare pair (`version_group` says whether the version numbers
 * are even comparable); optional-vs-nullable so `DocumentWithTags` and the
 * timeline's `DocumentRowMapped` both satisfy it, as with
 * `IfcDiffCandidateDoc`. */
export interface DrawingViewerCandidateDoc {
  id: string | number;
  filename: string;
  version?: number | null;
  version_group?: string | null;
  created_at?: string | null;
}

/** Parent DWG check. DWG child renditions are PDFs (and the list queries
 * filter `is_dwg_child` rows out anyway), so the extension is the whole test. */
export function isDwgParentDoc(d: { filename?: string | null }): boolean {
  return !!d.filename?.toLowerCase().endsWith(".dwg");
}

export type DocumentQuickViewKind = "preview" | "drawing" | null;

/** The list's eye/filename quick action. A DWG must never fall through to the
 * legacy child-PDF preview; without drawing-tool access it has no quick view. */
export function documentQuickViewKind(
  document: { filename?: string | null },
  drawingViewerEnabled: boolean,
  isRestore: boolean
): DocumentQuickViewKind {
  if (!isDwgParentDoc(document)) return "preview";
  return drawingViewerEnabled && !isRestore ? "drawing" : null;
}

/** Is a row's filename a click affordance at all? One predicate for every
 * surface that renders a document as "click to open", so the button and the
 * action can never disagree — a chip that opens nothing is the same dead click
 * as before, just moved. A DWG deliberately does NOT wait on
 * `canPreviewDocumentRow`: that gate is about the child PDF, and the drawing
 * tool reads the parent's own geometry. */
export function canQuickViewDocumentRow(
  document: { filename: string; content_type: string | null; state: string | null },
  drawingViewerEnabled: boolean,
  isRestore = false
): boolean {
  const kind = documentQuickViewKind(document, drawingViewerEnabled, isRestore);
  return kind === "drawing" || (kind === "preview" && canPreviewDocumentRow(document));
}

interface UseDrawingViewerResult {
  /** The actions are available at all (tool access gate). */
  canUseDrawingViewer: boolean;
  /** Exactly one parent DWG selected (and nothing else). */
  canViewDrawing: boolean;
  /** Exactly two parent DWGs selected (and nothing else). */
  canCompareDrawings: boolean;
  /** Navigate to the drawing tool for the given document ids (1 = view, 2 = compare). */
  openDrawingViewer: (ids: Array<string | number>) => void;
  /** Opens the single selected drawing. No-op when not available. */
  handleViewSelectedDrawing: () => void;
  /** Opens the compare view for the two selected drawings, older side first.
   *  No-op when not available. */
  handleCompareSelectedDrawings: () => void;
}

export function useDrawingViewer(
  /** Every document the view can resolve a selected id to (duplicates fine). */
  candidates: DrawingViewerCandidateDoc[],
  /** Selected document ids in selection order (`store.selectedDocumentIds`). */
  selectedIds: string[] | undefined,
  opts?: {
    /** Tool access (staff-only for now) — false hides/disables every action. */
    enabled?: boolean;
    /** Route into the building-scoped Drawings page (app chrome, back nav);
     *  without it the bare /tools/drawings route is used. */
    buildingId?: string;
  }
): UseDrawingViewerResult {
  const navigate = useNavigate();
  const { currentOrganization } = useOrganizationContext();
  const enabled = opts?.enabled ?? true;

  // Derived every render rather than memoized, same reasoning as
  // useIfcDiffOverview: an observer caller already re-renders on selection
  // changes.
  const byId = new Map(candidates.map((d) => [String(d.id), d]));
  const selectedDocs = (selectedIds ?? []).map((id) => byId.get(id));
  // A selected id we can't resolve counts as non-DWG (mixed selection).
  const allDwgParents =
    selectedDocs.length > 0 && selectedDocs.every((d) => d !== undefined && isDwgParentDoc(d));

  const canViewDrawing = enabled && allDwgParents && selectedDocs.length === 1;
  const canCompareDrawings = enabled && allDwgParents && selectedDocs.length === 2;

  // The project lookup below is a round trip with no pending UI, so the click
  // can be repeated or abandoned before it lands. Stamp each call and let only
  // the newest stamp navigate; unmount retires the stamp (-1 never matches a
  // stamp handed out) so a late resolve can't yank a user who moved on.
  const navStampRef = useRef(0);
  useEffect(
    () => () => {
      navStampRef.current = -1;
    },
    []
  );

  // Memoized: callers build per-row props in a useCallback keyed on this, so an
  // unstable identity would rebuild every timeline/table row on every render.
  const buildingIdOpt = opts?.buildingId;
  const orgId = currentOrganization?.id;
  const openDrawingViewer = useCallback(
    (ids: Array<string | number>) => {
      const query = `ids=${ids.map(String).join(",")}`;
      if (!buildingIdOpt) {
        navigate(`/tools/drawings?${query}`);
        return;
      }
      // `…/drawings` is a projects-only route, and Building view forgets the
      // remembered scope — so arriving without `?project=` stops at the shell's
      // "pick a project" gate and the ids are never read. Resolve the documents'
      // project and carry it, the way IfcDiffOverviewDialog opens the 3D viewer:
      // useBuildingScope treats a valid `?project=` on a non-index route as an
      // explicit deep link and adopts Projects mode with that scope.
      const to = (scope: string) => `/buildings/${buildingIdOpt}/drawings?${query}${scope}`;
      if (!orgId) {
        navigate(to(""));
        return;
      }
      const stamp = ++navStampRef.current;
      void fetchSeedDocs(orgId, ids.map(Number))
        .then((rows) => rows.find((r) => r.project_id != null)?.project_id ?? null)
        .catch(() => null)
        // A project we can't resolve still navigates — the gate then asks for a
        // pick, which beats swallowing the click. A superseded click doesn't:
        // its `?ids=` would clobber the newer one's, or land on a user who has
        // since navigated away.
        .then((projectId) => {
          if (stamp !== navStampRef.current) return;
          navigate(to(projectId != null ? `&project=${projectId}` : ""));
        });
    },
    [navigate, buildingIdOpt, orgId]
  );

  return {
    canUseDrawingViewer: enabled,
    canViewDrawing,
    canCompareDrawings,
    openDrawingViewer,
    handleViewSelectedDrawing: () => {
      if (canViewDrawing && selectedIds) openDrawingViewer([selectedIds[0]]);
    },
    handleCompareSelectedDrawings: () => {
      const [a, b] = selectedDocs;
      if (!canCompareDrawings || !a || !b) return;
      // `ids[0]` is authoritatively the OLDER side — useCompareController reads
      // it as the diff's base and the plaque labels that chip "Older". Tick
      // order says nothing about which revision is older, so order the pair the
      // way every other entry point does instead of trusting the clicks. Two
      // unrelated drawings are the common case here, and there `version_group`
      // is what stops "v4" being read as newer than another file's "v1".
      const [older, newer] = orderCandidatePair(
        {
          id: a.id,
          version: a.version ?? null,
          versionGroup: a.version_group ?? null,
          createdAt: a.created_at ?? null,
        },
        {
          id: b.id,
          version: b.version ?? null,
          versionGroup: b.version_group ?? null,
          createdAt: b.created_at ?? null,
        }
      );
      openDrawingViewer([older.id, newer.id]);
    },
  };
}
